import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getVehicleTyreLayout, type VehicleTyreLayout } from '../tyres.api';

export type VehicleTyreLayoutState =
  | { status: 'loading' }
  | { status: 'loaded'; layout: VehicleTyreLayout }
  | { status: 'no_layout'; message: string }
  | { status: 'forbidden' }
  | { status: 'error' };

function toErrorState(err: unknown): VehicleTyreLayoutState {
  if (err instanceof ApiError && err.status === 409) return { status: 'no_layout', message: 'This truck class or trailer type has no tyre layout yet.' };
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** A truck's or trailer's current tyre layout. A class or type without a layout answers 409, shown as its own state rather than as a failure. */
export function useVehicleTyreLayout(kind: 'truck' | 'trailer', id: string) {
  const [state, setState] = useState<VehicleTyreLayoutState>({ status: 'loading' });

  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', layout: await getVehicleTyreLayout(kind, id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }, [kind, id]);

  useEffect(() => {
    let cancelled = false;
    getVehicleTyreLayout(kind, id)
      .then((layout) => !cancelled && setState({ status: 'loaded', layout }))
      .catch((err) => !cancelled && setState(toErrorState(err)));
    return () => {
      cancelled = true;
    };
  }, [kind, id]);

  return { state, reload };
}
